import type { GameState, Player, BallColor, FrameResult } from './types';
import { BALL_VALUES, COLORS_PHASE_ORDER, INITIAL_REDS, MIN_FOUL_VALUE, DEFAULT_UNDO_LIMIT } from './constants';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clonePlayer(p: Player): Player {
  return { ...p };
}

function clonePlayers(players: [Player, Player]): [Player, Player] {
  return [clonePlayer(players[0]), clonePlayer(players[1])];
}

/** Strip undo stack before pushing to avoid deep cloning recursive state. */
function snapshot(state: GameState): GameState {
  return {
    ...state,
    players: clonePlayers(state.players),
    undoStack: [], // snapshots do not carry their own undo history
  };
}

function pushUndo(state: GameState, prev: GameState, undoLimit: number): GameState {
  const entry = snapshot(prev);
  const stack = [entry, ...state.undoStack].slice(0, undoLimit);
  return { ...state, undoStack: stack };
}

function opponent(idx: 0 | 1): 0 | 1 {
  return idx === 0 ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function createInitialState(
  p1Name: string,
  p2Name: string,
  framesTarget: number
): GameState {
  const makePlayer = (name: string): Player => ({
    name,
    frameScore: 0,
    currentBreak: 0,
    highestBreak: 0,
    framesWon: 0,
  });

  return {
    players: [makePlayer(p1Name), makePlayer(p2Name)],
    activePlayer: 0,
    phase: 'reds',
    redsRemaining: INITIAL_REDS,
    expectingColor: false,
    colorsPhaseNext: COLORS_PHASE_ORDER[0],
    undoStack: [],
    frameNumber: 1,
    framesTarget,
    frameComplete: false,
  };
}

/**
 * Pot a ball. Enforces phase rules:
 * - Reds phase: red → color → red → ...
 * - Colors phase: must follow COLORS_PHASE_ORDER in sequence.
 *
 * Returns state unchanged if the pot is illegal (so callers can show an error).
 */
export function potBall(
  state: GameState,
  ball: BallColor,
  undoLimit = DEFAULT_UNDO_LIMIT
): GameState {
  if (state.frameComplete) return state;

  const { redsRemaining, activePlayer } = state;

  // --- Apply the pot (no sequence validation — any ball can be potted freely) ---
  const players = clonePlayers(state.players);
  const active = players[activePlayer];
  const value = BALL_VALUES[ball];

  active.frameScore += value;
  active.currentBreak += value;
  if (active.currentBreak > active.highestBreak) {
    active.highestBreak = active.currentBreak;
  }

  const nextRedsRemaining = ball === 'red' ? Math.max(0, redsRemaining - 1) : redsRemaining;

  const next: GameState = {
    ...state,
    players,
    redsRemaining: nextRedsRemaining,
  };

  return pushUndo(next, state, undoLimit);
}

/**
 * Call a foul. Opponent receives max(4, ballValue) points.
 * Active player's break resets. Turn does NOT automatically switch —
 * the fouled player may choose to play again or make opponent play.
 * We follow the standard: turn switches to opponent.
 */
export function foul(
  state: GameState,
  ballValue = MIN_FOUL_VALUE,
  undoLimit = DEFAULT_UNDO_LIMIT
): GameState {
  if (state.frameComplete) return state;

  const players = clonePlayers(state.players);
  const { activePlayer } = state;
  const opp = opponent(activePlayer);

  const penalty = Math.max(MIN_FOUL_VALUE, ballValue);
  players[opp].frameScore += penalty;
  players[activePlayer].currentBreak = 0;

  const next: GameState = {
    ...state,
    players,
    activePlayer: opp,
  };

  return pushUndo(next, state, undoLimit);
}

/**
 * Free ball: awarded after a foul leaves opponent snookered.
 * The free ball counts as the ball on (1 point if on red, or the value
 * of the nominated color if on colors). Here we treat it as potting
 * the lowest available ball value (1 during reds phase, next color during
 * colors phase) — caller decides the nominated value via potBall afterwards.
 *
 * Implementation: a free ball is effectively a bonus — it counts as a red
 * (1pt) during the reds phase so the player can then pot a color.
 * During colors phase it counts as the value of the ball on.
 */
export function freeBall(
  state: GameState,
  undoLimit = DEFAULT_UNDO_LIMIT
): GameState {
  if (state.frameComplete) return state;

  const players = clonePlayers(state.players);
  const { activePlayer, phase } = state;
  const active = players[activePlayer];

  let value: number;
  let nextExpectingColor = state.expectingColor;
  let nextRedsRemaining = state.redsRemaining;

  if (phase === 'reds') {
    // Free ball acts as a red
    value = BALL_VALUES.red;
    nextExpectingColor = true;
    // Does NOT decrement redsRemaining (the free ball is not a real red)
  } else {
    // Free ball acts as the next color in sequence
    value = BALL_VALUES[state.colorsPhaseNext!];
  }

  active.frameScore += value;
  active.currentBreak += value;
  if (active.currentBreak > active.highestBreak) {
    active.highestBreak = active.currentBreak;
  }

  const next: GameState = {
    ...state,
    players,
    redsRemaining: nextRedsRemaining,
    expectingColor: nextExpectingColor,
  };

  return pushUndo(next, state, undoLimit);
}

/**
 * Miss called: active player's break resets, turn switches.
 */
export function missCalled(
  state: GameState,
  undoLimit = DEFAULT_UNDO_LIMIT
): GameState {
  if (state.frameComplete) return state;

  const players = clonePlayers(state.players);
  players[state.activePlayer].currentBreak = 0;

  const next: GameState = {
    ...state,
    players,
    activePlayer: opponent(state.activePlayer),
  };

  return pushUndo(next, state, undoLimit);
}

/**
 * Manually switch the active player (Enter key).
 * Resets the active player's current break.
 */
export function switchPlayer(
  state: GameState,
  undoLimit = DEFAULT_UNDO_LIMIT
): GameState {
  if (state.frameComplete) return state;

  const players = clonePlayers(state.players);
  players[state.activePlayer].currentBreak = 0;

  const next: GameState = {
    ...state,
    players,
    activePlayer: opponent(state.activePlayer),
  };

  return pushUndo(next, state, undoLimit);
}

/**
 * Active player concedes the frame. Opponent wins.
 */
export function concedeFrame(
  state: GameState,
  undoLimit = DEFAULT_UNDO_LIMIT
): GameState {
  if (state.frameComplete) return state;

  const players = clonePlayers(state.players);
  players[state.activePlayer].currentBreak = 0;

  const next: GameState = {
    ...state,
    players,
    frameComplete: true,
  };

  return pushUndo(next, state, undoLimit);
}

/**
 * Undo the last action. Pops the undo stack.
 */
export function undo(state: GameState): GameState {
  if (state.undoStack.length === 0) return state;
  const [prev, ...rest] = state.undoStack;
  // Restore previous snapshot; give it back the remaining undo history
  return { ...prev, undoStack: rest };
}

/**
 * End the current frame. Determines winner, updates framesWon,
 * returns a FrameResult and resets for next frame.
 *
 * Winner is determined by:
 * 1. If frameComplete via concede: opponent of the active player wins.
 * 2. Otherwise: higher frameScore wins. Tie goes to the non-active player
 *    (last ball scenario — in snooker the frame ends when one player is
 *    ahead and no balls remain; ties are decided by respotting the black,
 *    which we simplify to: player with higher score wins; if equal,
 *    player[0] wins as a default).
 */
export function endFrame(state: GameState): { state: GameState; frameResult: FrameResult } {
  const { players, activePlayer, frameNumber, framesTarget } = state;

  let winnerIndex: 0 | 1;

  if (state.frameComplete && !allBallsPotted(state)) {
    // Concede: opponent of active player wins
    winnerIndex = opponent(activePlayer);
  } else {
    winnerIndex =
      players[0].frameScore >= players[1].frameScore ? 0 : 1;
  }

  const loserIndex = opponent(winnerIndex);

  // Determine highest break in this frame
  let highestBreak = 0;
  let highestBreakPlayer = '';
  for (const p of players) {
    if (p.highestBreak > highestBreak) {
      highestBreak = p.highestBreak;
      highestBreakPlayer = p.name;
    }
  }

  const frameResult: FrameResult = {
    winnerIndex,
    winnerName: players[winnerIndex].name,
    loserName: players[loserIndex].name,
    player1Score: players[0].frameScore,
    player2Score: players[1].frameScore,
    highestBreak,
    highestBreakPlayer,
    frameNumber,
  };

  // Build next-frame state
  const nextPlayers: [Player, Player] = [
    {
      name: players[0].name,
      frameScore: 0,
      currentBreak: 0,
      highestBreak: 0,
      framesWon: players[0].framesWon + (winnerIndex === 0 ? 1 : 0),
    },
    {
      name: players[1].name,
      frameScore: 0,
      currentBreak: 0,
      highestBreak: 0,
      framesWon: players[1].framesWon + (winnerIndex === 1 ? 1 : 0),
    },
  ];

  const nextState: GameState = {
    players: nextPlayers,
    activePlayer: 0,
    phase: 'reds',
    redsRemaining: INITIAL_REDS,
    expectingColor: false,
    colorsPhaseNext: COLORS_PHASE_ORDER[0],
    undoStack: [],
    frameNumber: frameNumber + 1,
    framesTarget,
    frameComplete: false,
  };

  return { state: nextState, frameResult };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function allBallsPotted(state: GameState): boolean {
  return (
    state.phase === 'colors' &&
    state.colorsPhaseNext === null &&
    state.frameComplete
  );
}

/**
 * Check whether the colors phase should be entered.
 * Called internally after potting in reds phase.
 */
export function shouldSwitchToColors(state: GameState): boolean {
  return state.phase === 'reds' && state.redsRemaining === 0 && !state.expectingColor;
}

/**
 * Returns the current point lead of the active player over the opponent.
 * Negative means the active player is trailing.
 */
export function pointsAhead(state: GameState): number {
  return (
    state.players[state.activePlayer].frameScore -
    state.players[opponent(state.activePlayer)].frameScore
  );
}

/**
 * Returns the maximum points still available on the table.
 */
export function pointsRemaining(state: GameState): number {
  if (state.frameComplete) return 0;
  // 147 − total points scored so far.
  // Decreases by exactly the value of each ball potted — simple and predictable
  // for a club scoreboard (the "pro max-remaining" approach confused users by
  // dropping 7pts on a yellow pot instead of 2).
  const scored = state.players[0].frameScore + state.players[1].frameScore;
  return Math.max(0, 147 - scored);
}
